import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import css from './TestimonialConfirmPage.css';
import config from '../../config';
import PageWrapper from '../../custom-components/wrappers/page-wrapper/PageWrapper';
import { IconSpinner } from '../../components';
import { previewTestimonial, confirmTestimonialWithEdit } from '../../custom-ducks/testimonials.duck';
import { POST_AUTH_REDIRECT_KEY } from '../../util/postAuthRedirect';

const BODY_MIN_LENGTH = 30;
const BODY_MAX_LENGTH = 2000;

// Errors that happen while editing are recoverable (fix the text, log in
// with the right account, ask for a fresh link) — keep the referee in the
// editing view with an inline banner instead of nuking their typed edit to
// a full-page error state.
const INLINE_EDIT_ERROR_REASONS = ['identity-mismatch', 'invalid-body-length', 'expired', 'unavailable'];

const errorCopy = (reason) => {
  switch (reason) {
    case 'invalid':
      return {
        title: "This link isn't valid",
        body: 'The confirmation link you clicked is missing or has been used in a way we didn\'t expect. If you believe this is a mistake, please reply to the email you received.',
      };
    case 'expired':
      return {
        title: 'This link has expired',
        body: 'Confirmation links are valid for 14 days. If you\'d still like to confirm this testimonial, please ask the coach to send a fresh request.',
      };
    case 'unavailable':
      return {
        title: 'Already handled',
        body: 'This testimonial has already been declined or revoked. No further action is needed.',
      };
    case 'missing-index':
      return {
        title: 'Verification system is initialising',
        body: 'The verification system is being set up for the first time. A Viveka administrator has been notified and will resolve this shortly — please try the link again in a few minutes.',
      };
    case 'identity-mismatch':
      return {
        title: 'Different account',
        body: 'This edit link is tied to a different Viveka account. Please log in with the email address the testimonial was sent to, or use "Looks good, confirm" instead if the text is fine as-is.',
      };
    case 'invalid-body-length':
      return {
        title: 'Text too short or too long',
        body: `Please write between ${BODY_MIN_LENGTH} and ${BODY_MAX_LENGTH} characters.`,
      };
    default:
      return {
        title: 'Something went wrong',
        body: 'We couldn\'t process this confirmation. Please try again in a few minutes, or reply to the email you received.',
      };
  }
};

// Confirm/edit page for a coach-authored testimonial. The referee opens
// this from the email link (/testimonial/confirm?t={token}). No login is
// required to view the preview or confirm it as-is; logging in is only
// required if they want to edit the wording first (one edit, ever — the
// edit and the confirmation happen as a single action).
function TestimonialConfirmPage({ location, history, isAuthenticated, onPreview, onConfirmWithEdit }) {
  const [state, setState] = useState({ status: 'loading' });
  const [coachName, setCoachName] = useState('');
  const [bodyText, setBodyText] = useState('');
  const [editedBody, setEditedBody] = useState('');
  const [editError, setEditError] = useState(null);

  const token = new URLSearchParams((location && location.search) || '').get('t');

  // Shared by the "Looks good, confirm" button on the preview screen and by
  // the ?auto=1 email link, which already showed the referee the full text
  // inline in the email itself — so it skips straight to confirming instead
  // of making them click through an identical-looking preview page again.
  function runConfirm() {
    setState((prev) => ({ ...prev, status: 'confirming' }));
    axios
      .get(`${config.coachSaaSService}testimonials/confirm`, { params: { t: token } })
      .then((res) => {
        const data = res.data || {};
        if (data.ok) {
          setCoachName((prevName) => data.coachName || prevName);
          setState({ status: data.alreadyConfirmed ? 'already' : 'success' });
        } else {
          setState({ status: 'error', reason: data.reason || 'unknown', detail: data.detail || null, adminFixUrl: data.adminFixUrl || null });
        }
      })
      .catch((err) => {
        const data = err?.response?.data || {};
        setState({ status: 'error', reason: data.reason || 'unknown', detail: data.detail || null, adminFixUrl: data.adminFixUrl || null });
      });
  }

  useEffect(() => {
    if (!token) {
      setState({ status: 'error', reason: 'invalid' });
      return;
    }

    onPreview(token)
      .then((res) => {
        const data = res.data || {};
        if (!data.ok) {
          setState({ status: 'error', reason: data.reason || 'unknown', detail: data.detail || null, adminFixUrl: data.adminFixUrl || null });
          return;
        }

        setCoachName(data.coachName || '');
        setBodyText(data.body || '');

        if (data.alreadyConfirmed) {
          setState({ status: 'already' });
          return;
        }

        const params = new URLSearchParams((location && location.search) || '');
        const wantsEdit = isAuthenticated && params.get('edit') === '1';
        if (wantsEdit) {
          setEditedBody(data.body || '');
          setState({ status: 'editing' });
        } else if (params.get('auto') === '1') {
          runConfirm();
        } else {
          setState({ status: 'preview' });
        }
      })
      .catch((err) => {
        const data = err?.response?.data || {};
        setState({ status: 'error', reason: data.reason || 'unknown', detail: data.detail || null, adminFixUrl: data.adminFixUrl || null });
      });
  }, []);

  const handleConfirmAsIs = () => runConfirm();

  const handleEditClick = () => {
    if (!isAuthenticated) {
      const params = new URLSearchParams((location && location.search) || '');
      params.set('edit', '1');
      const returnUrl = `${location.pathname}?${params.toString()}`;
      try {
        window.localStorage.setItem(POST_AUTH_REDIRECT_KEY, returnUrl);
      } catch (e) {
        // localStorage may be disabled — non-fatal, they'll just land on
        // the default post-login page instead of bouncing back here.
      }
      history.push('/login');
      return;
    }

    setEditedBody(bodyText);
    setEditError(null);
    setState((prev) => ({ ...prev, status: 'editing' }));
  };

  const handleSubmitEdit = () => {
    const trimmed = (editedBody || '').trim();
    if (trimmed.length < BODY_MIN_LENGTH || trimmed.length > BODY_MAX_LENGTH) {
      setEditError(errorCopy('invalid-body-length').body);
      return;
    }

    setEditError(null);
    setState((prev) => ({ ...prev, status: 'confirming' }));
    onConfirmWithEdit(token, trimmed)
      .then((res) => {
        const data = res.data || {};
        if (data.ok) {
          setCoachName(data.coachName || coachName);
          setState({ status: data.alreadyConfirmed ? 'already' : 'success', wasEdited: !data.alreadyConfirmed });
        } else {
          const reason = data.reason || 'unknown';
          if (INLINE_EDIT_ERROR_REASONS.includes(reason)) {
            setEditError(errorCopy(reason).body);
            setState((prev) => ({ ...prev, status: 'editing' }));
          } else {
            setState({ status: 'error', reason, detail: data.detail || null, adminFixUrl: data.adminFixUrl || null });
          }
        }
      })
      .catch((err) => {
        const data = err?.response?.data || {};
        const reason = data.reason || 'unknown';
        if (INLINE_EDIT_ERROR_REASONS.includes(reason)) {
          setEditError(errorCopy(reason).body);
          setState((prev) => ({ ...prev, status: 'editing' }));
        } else {
          setState({ status: 'error', reason, detail: data.detail || null, adminFixUrl: data.adminFixUrl || null });
        }
      });
  };

  return (
    <PageWrapper>
      <div className={css.base}>
        <div className={css.card}>
          {(state.status === 'loading' || state.status === 'confirming') && (
            <>
              <div className={css.spinnerWrap}>
                <IconSpinner />
              </div>
              <p className={css.title}>{state.status === 'confirming' ? 'Submitting…' : 'Loading your testimonial…'}</p>
              <p className={css.body}>This will only take a second.</p>
            </>
          )}

          {state.status === 'preview' && (
            <>
              <h1 className={css.title}>Please review your testimonial</h1>
              <p className={css.body}>
                {coachName || 'Your coach'} shared this testimonial and would like you to confirm it's accurate.
              </p>
              <p className={css.metaLabel}>The testimonial</p>
              <p className={css.quote}>{bodyText}</p>
              <div className={css.actions}>
                <button type="button" className={css.btnPrimary} onClick={handleConfirmAsIs}>
                  Looks good, confirm
                </button>
                <button type="button" className={css.btnSecondary} onClick={handleEditClick}>
                  Edit before confirming
                </button>
              </div>
              <p className={css.subBody}>
                Editing requires logging in to your Viveka account. You can only edit this once.
              </p>
            </>
          )}

          {state.status === 'editing' && (
            <>
              <h1 className={css.title}>Edit your testimonial</h1>
              <p className={css.body}>
                Make any changes you'd like, then confirm. This is a one-time edit — once you confirm, the wording is final.
              </p>
              {editError && <div className={css.inlineError}>{editError}</div>}
              <textarea
                className={css.textarea}
                value={editedBody}
                onChange={(e) => setEditedBody(e.target.value)}
                maxLength={BODY_MAX_LENGTH}
              />
              <p className={css.charCount}>
                {editedBody.trim().length}/{BODY_MAX_LENGTH} characters (minimum {BODY_MIN_LENGTH})
              </p>
              <div className={css.actions}>
                <button type="button" className={css.btnPrimary} onClick={handleSubmitEdit}>
                  Save &amp; confirm
                </button>
              </div>
            </>
          )}

          {state.status === 'success' && (
            <>
              <div className={css.iconBig}>&#10003;</div>
              <h1 className={css.title}>Thank you!</h1>
              <p className={css.body}>
                Your testimonial has been confirmed{coachName ? ` and will appear on ${coachName}'s Viveka profile` : ''}
                {state.wasEdited ? ', including the changes you made' : ''}.
                There's nothing else you need to do.
              </p>
              <p className={css.subBody}>
                {state.wasEdited
                  ? 'You can close this tab.'
                  : "You don't need a Viveka account — you can close this tab."}
              </p>
            </>
          )}

          {state.status === 'already' && (
            <>
              <div className={css.iconBig}>&#10003;</div>
              <h1 className={css.title}>Already confirmed</h1>
              <p className={css.body}>
                This testimonial was already confirmed{coachName ? ` for ${coachName}` : ''} &mdash; thank you. You can close this tab safely.
              </p>
            </>
          )}

          {state.status === 'error' && (
            <>
              <div className={css.iconBigError}>!</div>
              <h1 className={css.title}>{errorCopy(state.reason).title}</h1>
              <p className={css.body}>{errorCopy(state.reason).body}</p>

              {/* Tiny debug strip — shows the actual backend reason
                  code in faint type. If the backend returned an
                  adminFixUrl (missing-index case), surface it as a
                  small link so a Viveka admin can resolve it in one
                  click without digging through Cloud Run logs. */}
              <div className={css.debugStrip}>
                <span>Error code: {state.reason}</span>
                {state.adminFixUrl && (
                  <>
                    {' · '}
                    <a
                      href={state.adminFixUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={css.debugLink}
                    >
                      Admin: create required index
                    </a>
                  </>
                )}
                {state.detail && (
                  <details className={css.debugDetails}>
                    <summary>technical detail</summary>
                    <code>{state.detail}</code>
                  </details>
                )}
              </div>
            </>
          )}

          <div className={css.footer}>
            <span className={css.brand}>
              Viveka<span className={css.brandDot}>.</span>
            </span>
            <span className={css.tagline}>The infrastructure for human development.</span>
          </div>
        </div>
      </div>
    </PageWrapper>
  );
}

const mapStateToProps = (state) => {
  const { isAuthenticated } = state.Auth;
  return { isAuthenticated };
};

const mapDispatchToProps = (dispatch) => ({
  onPreview: (token) => dispatch(previewTestimonial(token)),
  onConfirmWithEdit: (token, body) => dispatch(confirmTestimonialWithEdit(token, body)),
});

export default compose(
  withRouter,
  connect(mapStateToProps, mapDispatchToProps)
)(TestimonialConfirmPage);
